"use client"

import * as React from "react"
import { Link } from "react-router-dom"

import { cn, MobileScreenHeader } from "@ibada/ui"
import { type DataTableToolbarConfig } from "@ibada/ui/components/data-table"

type MobileListHeader = DataTableToolbarConfig<unknown>["mobileHeader"]

/** A section's phone chrome: its title row and the tabs under it, handed down to whichever tab is showing. */
export type MobileListChrome = {
  mobileHeader: NonNullable<MobileListHeader>
  mobileTabs?: React.ReactNode
}

const MobileListChromeContext = React.createContext<MobileListChrome | null>(null)

/**
 * Provided by a tabbed section's layout on phones. A tab that is a table hands it to its toolbar (so ⋯ and +
 * sit beside the title instead of floating on their own row); a tab that is not draws it with
 * `MobileListChromeHeader`.
 */
export const MobileListChromeProvider = MobileListChromeContext.Provider

export function useMobileListChrome(): MobileListChrome | null {
  return React.useContext(MobileListChromeContext)
}

/**
 * The section's title row and tabs, for a tab that is not a table: the shared MobileScreenHeader, pinned, with the
 * tabs pinned under it. `actions` sit beside the title (two at most).
 */
export function MobileListChromeHeader({ actions }: { actions?: React.ReactNode }) {
  const chrome = useMobileListChrome()
  if (!chrome) return null
  return (
    <MobileScreenHeader
      title={chrome.mobileHeader.title}
      subtitle={chrome.mobileHeader.subtitle}
      back={chrome.mobileHeader.back}
      actions={actions}
    >
      {chrome.mobileTabs}
    </MobileScreenHeader>
  )
}

/** Phone tabs as an iOS segmented control of links (each tab stays a real, deep-linkable path). */
export function MobileSegmentedLinks<TValue extends string>({
  value,
  items,
}: {
  value: TValue
  items: { value: TValue; label: string; href: string }[]
}) {
  return (
    <div role='tablist' className='flex gap-0.5 rounded-[0.875rem] bg-[rgba(118,118,128,0.12)] p-[3px] dark:bg-[rgba(118,118,128,0.24)]'>
      {items.map((item) => (
        <Link
          key={item.value}
          role='tab'
          aria-selected={item.value === value}
          to={item.href}
          replace
          className={cn(
            "flex h-9 flex-1 items-center justify-center rounded-[0.6875rem] text-[13px] font-bold transition-colors",
            item.value === value ? "bg-card font-extrabold text-foreground shadow-sm" : "text-muted-foreground",
          )}
        >
          {item.label}
        </Link>
      ))}
    </div>
  )
}

/**
 * The same iOS segmented control for a switch that is NOT a page (two lists inside one wizard step): buttons
 * driving local state. A page's tabs use `MobileSegmentedLinks` so each stays a real path.
 */
export function MobileSegmentedControl<TValue extends string>({
  value,
  items,
  onChange,
}: {
  value: TValue
  items: { value: TValue; label: string }[]
  onChange: (value: TValue) => void
}) {
  return (
    <div role='tablist' className='flex gap-0.5 rounded-[0.875rem] bg-[rgba(118,118,128,0.12)] p-[3px] dark:bg-[rgba(118,118,128,0.24)]'>
      {items.map((item) => (
        <button
          key={item.value}
          type='button'
          role='tab'
          aria-selected={item.value === value}
          onClick={() => onChange(item.value)}
          className={cn(
            "flex h-9 min-w-0 flex-1 items-center justify-center truncate rounded-[0.6875rem] px-2 text-[13px] font-bold transition-colors",
            item.value === value ? "bg-card font-extrabold text-foreground shadow-sm" : "text-muted-foreground",
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Phone tabs when there are too many for a segmented control (four or more sections): one row of chips that
 * scrolls sideways, in the list chips' style. Each chip is a real, deep-linkable path.
 */
export function MobileChipLinks<TValue extends string>({
  value,
  items,
}: {
  value: TValue
  items: { value: TValue; label: string; href: string }[]
}) {
  const activeRef = React.useRef<HTMLAnchorElement>(null)
  React.useEffect(() => {
    // Keep the current section in view when the row opens scrolled away from it.
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "center" })
  }, [value])
  return (
    // `py-1 -my-1`: a sideways scroller also clips vertically, which cut the active chip's ring at top and bottom.
    <div role='tablist' className='-mx-4 -my-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-4 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
      {items.map((item) => {
        const active = item.value === value
        return (
          <Link
            key={item.value}
            ref={active ? activeRef : undefined}
            role='tab'
            aria-selected={active}
            to={item.href}
            replace
            className={cn(
              "inline-flex h-9 shrink-0 items-center rounded-full px-3.5 text-[13px] whitespace-nowrap transition-colors",
              active ? "bg-primary/12 font-extrabold text-primary ring-1 ring-primary/25" : "bg-card font-bold text-muted-foreground ring-1 ring-border/60",
            )}
          >
            {item.label}
          </Link>
        )
      })}
    </div>
  )
}
